/* search: the overlay door behind the header lens, and the live answers
   under every search field. vanilla and deferred. nothing depends on it:
   each lens is a real link to the search page and each field a real
   form, so without this file the site still searches. */
(function () {
  'use strict';

  var SECTION = 'predictive-search';
  var MIN = 2;      // characters before we ask
  var WAIT = 200;   // ms of quiet before we ask

  function suggestUrl(base, q) {
    var p = new URLSearchParams();
    p.set('q', q);
    p.set('section_id', SECTION);
    p.set('resources[type]', 'article,product,page,query');
    p.set('resources[limit]', '6');
    p.set('resources[limit_scope]', 'each');
    p.set('resources[options][unavailable_products]', 'hide');
    p.set('resources[options][fields]', 'title,body,tag,author,product_type,variants.title');
    return base + '?' + p.toString();
  }

  /* one live field: the overlay has one, the search page has one */
  function Box(form) {
    this.form = form;
    this.key = form.getAttribute('data-predictive') || 'ps';
    this.base = form.getAttribute('data-suggest-url') || '/search/suggest';
    this.input = form.querySelector('.ps-input');
    this.clearBtn = form.querySelector('[data-ps-clear]');
    this.panel = form.querySelector('[data-ps-panel]');
    this.list = form.querySelector('[data-ps-list]');
    this.empty = form.querySelector('[data-ps-empty]');
    this.foot = form.querySelector('[data-ps-foot]');
    this.status = form.querySelector('[data-ps-status]');
    this.floating = form.classList.contains('ps--page');
    this.timer = 0;
    this.ctrl = null;
    this.cache = {};
    this.shown = '';
    this.active = -1;
    if (this.input && this.panel && this.list) this.bind();
  }

  Box.prototype.bind = function () {
    var self = this;
    this.syncClear();
    this.input.addEventListener('input', function () {
      self.syncClear();
      self.schedule();
    });
    this.input.addEventListener('keydown', function (e) { self.keys(e); });
    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', function () {
        self.input.value = '';
        self.syncClear();
        self.reset();
        self.say('');
        self.input.focus();
      });
    }
    if (this.floating) {
      // the page field drops its answers when attention goes elsewhere
      document.addEventListener('click', function (e) {
        if (!self.form.contains(e.target)) self.hide();
      });
      this.input.addEventListener('focus', function () {
        if (self.shown && self.shown === self.input.value.trim()) self.show();
      });
    }
  };

  Box.prototype.syncClear = function () {
    if (this.clearBtn) this.clearBtn.hidden = this.input.value.length === 0;
  };

  Box.prototype.schedule = function () {
    var self = this;
    var q = this.input.value.trim();
    clearTimeout(this.timer);
    if (q.length < MIN) {
      if (this.ctrl) this.ctrl.abort();
      this.reset();
      this.say('');
      return;
    }
    if (this.cache[q]) { this.render(q, this.cache[q]); return; }
    this.timer = setTimeout(function () { self.ask(q); }, WAIT);
  };

  Box.prototype.ask = function (q) {
    var self = this;
    if (this.ctrl) this.ctrl.abort();
    this.ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    fetch(suggestUrl(this.base, q), {
      credentials: 'same-origin',
      signal: this.ctrl ? this.ctrl.signal : undefined
    })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
      .then(function (html) {
        self.cache[q] = html;
        if (self.input.value.trim() === q) self.render(q, html);
      })
      .catch(function () { /* aborted or offline: the form still works */ });
  };

  Box.prototype.render = function (q, html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var root = doc.querySelector('[data-ps-root]');
    if (!root) { this.reset(); return; }
    var key = this.key;
    var groups = root.querySelector('[data-ps-groups]');
    var count = parseInt(root.getAttribute('data-ps-count'), 10) || 0;
    var line = root.getAttribute('data-ps-status') || '';

    // two fields can share a page, so every id gets this field's prefix
    groups.querySelectorAll('[id]').forEach(function (el) { el.id = key + '-' + el.id; });
    groups.querySelectorAll('[aria-labelledby]').forEach(function (el) {
      el.setAttribute('aria-labelledby', key + '-' + el.getAttribute('aria-labelledby'));
    });
    var opts = groups.querySelectorAll('[role="option"]');
    opts.forEach(function (el, i) { el.id = key + '-opt-' + i; });

    this.list.textContent = '';
    while (groups.firstChild) this.list.appendChild(document.adoptNode(groups.firstChild));
    this.list.hidden = opts.length === 0;

    this.empty.hidden = count > 0;
    this.empty.textContent = count > 0 ? '' : line;

    this.foot.textContent = '';
    var all = root.querySelector('[data-ps-all]');
    if (all && count > 0) this.foot.appendChild(document.adoptNode(all));

    this.shown = q;
    this.active = -1;
    this.input.removeAttribute('aria-activedescendant');
    this.show();
    this.say(line);
  };

  Box.prototype.options = function () {
    return this.panel.hidden ? [] : Array.prototype.slice.call(this.list.querySelectorAll('[role="option"]'));
  };

  Box.prototype.show = function () {
    if (!this.shown) return;
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', this.list.hidden ? 'false' : 'true');
  };

  Box.prototype.hide = function () {
    this.setActive(-1);
    this.panel.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
  };

  Box.prototype.reset = function () {
    clearTimeout(this.timer);
    this.shown = '';
    this.list.textContent = '';
    this.list.hidden = true;
    this.empty.hidden = true;
    this.foot.textContent = '';
    this.hide();
  };

  Box.prototype.setActive = function (i) {
    var opts = Array.prototype.slice.call(this.list.querySelectorAll('[role="option"]'));
    opts.forEach(function (el) { el.setAttribute('aria-selected', 'false'); });
    this.active = i;
    if (i < 0 || !opts[i]) {
      this.active = -1;
      this.input.removeAttribute('aria-activedescendant');
      return;
    }
    opts[i].setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', opts[i].id);
    opts[i].scrollIntoView({ block: 'nearest' });
  };

  Box.prototype.keys = function (e) {
    var opts = this.options();
    var n = opts.length;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!n) return;
      e.preventDefault();
      var i = this.active;
      if (e.key === 'ArrowDown') i = i < n - 1 ? i + 1 : 0;
      else i = i > 0 ? i - 1 : (i === 0 ? -1 : n - 1);
      this.setActive(i);
    } else if (e.key === 'Enter') {
      // a highlighted answer opens; otherwise the form goes to the full page
      if (this.active >= 0 && opts[this.active]) {
        e.preventDefault();
        window.location.href = opts[this.active].href;
      }
    } else if (e.key === 'Escape' && this.floating && !this.panel.hidden) {
      e.preventDefault();
      this.hide();
    }
  };

  // the status line is re-set after a beat so the same count is re-read
  Box.prototype.say = function (text) {
    var s = this.status;
    if (!s) return;
    s.textContent = '';
    if (text) setTimeout(function () { s.textContent = text; }, 60);
  };

  var boxes = [];
  document.querySelectorAll('form[data-predictive]').forEach(function (f) { boxes.push(new Box(f)); });

  /* the overlay: a native modal dialog, opened from any lens */
  var dialog = document.querySelector('[data-search-overlay]');
  if (!dialog || typeof dialog.showModal !== 'function') return;
  var root = document.documentElement;
  var box = null;
  boxes.forEach(function (b) { if (dialog.contains(b.form)) box = b; });
  if (!box) return;
  var opener = null;
  var downOnBackdrop = false;

  // folded details content can still report boxes, so a closed
  // details counts as out of sight whatever the boxes say
  function visible(el) {
    if (!el || !el.isConnected || el.closest('details:not([open])')) return false;
    if (typeof el.checkVisibility === 'function') return el.checkVisibility({ visibilityProperty: true });
    return el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';
  }
  function lens() {
    var all = document.querySelectorAll('a.search-link');
    for (var i = 0; i < all.length; i++) if (visible(all[i])) return all[i];
    return null;
  }
  function open(from) {
    if (dialog.open) return;
    // opened from inside the phone menu: fold the menu away first
    var menu = document.querySelector('.menu.nav-mobile');
    if (menu && menu.open) { menu.open = false; document.body.classList.remove('menu-open'); }
    // the way back is a lens she can see; the dialog remembers what had
    // focus when it opened, so that is the lens, not a folded menu link
    opener = visible(from) ? from : lens();
    if (opener && document.activeElement !== opener) opener.focus({ preventScroll: true });
    dialog.showModal();
    root.classList.add('search-open');
    box.input.focus();
    if (box.input.value) box.input.select();
    if (box.shown) box.show();
  }
  function close() {
    if (dialog.open) dialog.close();
  }

  dialog.addEventListener('close', function () {
    root.classList.remove('search-open');
    box.setActive(-1);
    // the page behind is still inert while close fires, so wait a beat
    setTimeout(function () {
      var back = visible(opener) ? opener : lens();
      if (back && document.activeElement !== back) back.focus();
    }, 0);
  });
  dialog.addEventListener('cancel', function (e) { e.preventDefault(); close(); });
  dialog.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') { e.preventDefault(); close(); }
  });
  // a click on the dim ground outside the panel closes; a drag that ends there does not
  dialog.addEventListener('pointerdown', function (e) { downOnBackdrop = e.target === dialog; });
  dialog.addEventListener('click', function (e) {
    if (e.target === dialog && downOnBackdrop) close();
    downOnBackdrop = false;
  });
  var closeBtn = dialog.querySelector('[data-search-close]');
  if (closeBtn) closeBtn.addEventListener('click', close);

  document.addEventListener('click', function (e) {
    var a = e.target.closest ? e.target.closest('a.search-link, [data-search-open]') : null;
    if (!a) return;
    // a modified click still opens the search page in a new tab
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    open(a);
  });

  // coming back through history, the page should not arrive locked
  window.addEventListener('pageshow', function (e) { if (e.persisted) close(); });
})();
